'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { signInWithEmail, signUpWithEmail, signInWithGoogle, signInWithTwitter, resetPassword } from '@/lib/auth';
import { auth, db } from '@/lib/firebase';
import { onAuthStateChanged, User } from 'firebase/auth';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { getModelImageUrl, getModelVideoUrl } from '@/lib/image-utils';

interface ModelData {
  id: string;
  gender: string;
  name: string;
  age?: number;
  shortDescription: string;
  mainImagePath: string;
  idleVideoPath?: string;
  isNew?: boolean;
  storyTag?: string;
}

export default function Home() {
  const router = useRouter();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'girls' | 'boys' | 'anime'>('girls');
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState('');
  const [models, setModels] = useState<ModelData[]>([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [videoReadyStates, setVideoReadyStates] = useState<Record<string, boolean>>({});
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const [showAgeVerification, setShowAgeVerification] = useState(false);
  const [showCookieConsent, setShowCookieConsent] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const [displayedModels, setDisplayedModels] = useState<ModelData[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreToLoad, setHasMoreToLoad] = useState(true);

  // Check authentication state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Reset navigation state with timeout fallback
  useEffect(() => {
    if (isNavigating) {
      const timeout = setTimeout(() => {
        setIsNavigating(false);
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [isNavigating]);

  // Cookie helper functions
  const setCookie = (name: string, value: string, days: number) => {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
  };

  const getCookie = (name: string): string | null => {
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i];
      while (cookie.charAt(0) === ' ') cookie = cookie.substring(1);
      if (cookie.indexOf(nameEQ) === 0) {
        return cookie.substring(nameEQ.length, cookie.length);
      }
    }
    return null;
  };

  // Check age verification and cookie consent on mount
  useEffect(() => {
    const ageVerified = getCookie('ageVerified');
    const cookieConsent = getCookie('cookieConsent');
    
    if (!ageVerified) {
      setShowAgeVerification(true);
    } else if (!cookieConsent) {
      setShowCookieConsent(true);
    }
  }, []);

  const handleAgeConfirm = () => {
    // Save age verification in cookie for 1 year
    setCookie('ageVerified', 'true', 365);
    setShowAgeVerification(false);
    
    // Show cookie consent after age verification
    const cookieConsent = getCookie('cookieConsent');
    if (!cookieConsent) {
      setShowCookieConsent(true);
    }
  };

  const handleUnderAge = () => {
    window.location.href = 'https://www.google.com';
  };

  const handleCookieConsent = (type: 'all' | 'necessary') => {
    // Save cookie consent choice for 1 year
    setCookie('cookieConsent', type, 365);
    setShowCookieConsent(false);
  };

  // Set initial sidebar state based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 968) {
        setSidebarOpen(true); // Open on desktop
        setIsMobile(false);
      } else {
        setSidebarOpen(false); // Closed on mobile
        setIsMobile(true);
      }
    };

    // Set initial state
    handleResize();

    // Listen for window resize
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Intersection Observer for lazy loading more models
  useEffect(() => {
    if (typeof window === 'undefined' || !hasMoreToLoad) return;

    const loadMoreTrigger = document.querySelector('.load-more-trigger');
    if (!loadMoreTrigger) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && hasMoreToLoad && !loadingMore) {
            loadMoreModels();
          }
        });
      },
      {
        root: null,
        rootMargin: '200px',
        threshold: 0.1,
      }
    );

    observer.observe(loadMoreTrigger);

    return () => {
      observer.disconnect();
    };
  }, [hasMoreToLoad, loadingMore, displayedModels]);

  // Intersection Observer for mobile video preloading
  useEffect(() => {
    if (typeof window === 'undefined' || displayedModels.length === 0) return;

    const isMobileDevice = window.innerWidth <= 968;
    if (!isMobileDevice) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target.querySelector('video') as HTMLVideoElement;
          if (video && entry.isIntersecting) {
            // Preload video when it enters viewport
            if (video.readyState < 3) {
              video.load();
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '50px',
        threshold: 0.1,
      }
    );

    const cards = document.querySelectorAll('.character-card');
    cards.forEach((card) => observer.observe(card));

    return () => {
      cards.forEach((card) => observer.unobserve(card));
    };
  }, [displayedModels]);

  // Fetch models from Firestore based on selected gender
  useEffect(() => {
    const fetchModels = async () => {
      setModelsLoading(true);
      setDisplayedModels([]);
      setHasMoreToLoad(true);
      try {
        const modelsRef = collection(db, 'models');
        const q = query(
          modelsRef,
          where('gender', '==', activeTab),
           where('modelType', '!=', 'private'),
          orderBy('createdAt', 'desc')
        );
        
        const querySnapshot = await getDocs(q);
        const fetchedModels: ModelData[] = [];
        
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          fetchedModels.push({
            id: doc.id,
            gender: data.gender,
            name: data.name,
            age: typeof data.age === 'number' ? data.age : parseInt(data.age) || 22,
            shortDescription: data.shortDescription,
            mainImagePath: data.mainImagePath || '/girls/img (1).png',
            idleVideoPath: data.idleVideoPath,
            isNew: checkIfNew(data.createdAt),
            storyTag: data.storyTag || '',
          });
        });
        
        setModels(fetchedModels);
        
        // Load initial batch based on screen size
        const initialBatchSize = window.innerWidth <= 968 ? 4 : 8;
        setDisplayedModels(fetchedModels.slice(0, initialBatchSize));
        setHasMoreToLoad(fetchedModels.length > initialBatchSize);
      } catch (error) {
        console.error('Error fetching models:', error);
      } finally {
        setModelsLoading(false);
      }
    };

    fetchModels();
  }, [activeTab]);

  // Check if model is new (created within last 7 days)
  const checkIfNew = (createdAt: any): boolean => {
    if (!createdAt) return false;
    const createdDate = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
    const daysDiff = (Date.now() - createdDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysDiff <= 7;
  };

  // Load more models
  const loadMoreModels = () => {
    if (loadingMore || !hasMoreToLoad) return;
    
    setLoadingMore(true);
    setTimeout(() => {
      const currentCount = displayedModels.length;
      const batchSize = window.innerWidth <= 968 ? 4 : 8;
      const nextBatch = models.slice(currentCount, currentCount + batchSize);
      
      if (nextBatch.length > 0) {
        setDisplayedModels(prev => [...prev, ...nextBatch]);
        setHasMoreToLoad(currentCount + nextBatch.length < models.length);
      } else {
        setHasMoreToLoad(false);
      }
      setLoadingMore(false);
    }, 300);
  };

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showAuthModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showAuthModal]);


  const faqData = [
    {
      question: 'What is lovelybabe.ai?',
      answer: 'lovelybabe.ai is an advanced AI character platform where you can interact with diverse AI personalities, create your own characters, and explore endless conversations.',
    },
    {
      question: 'Is it free to use?',
      answer: 'Yes! We offer a free tier with basic features. Premium membership unlocks advanced features, more interactions, and exclusive characters.',
    },
    {
      question: 'Can I create my own characters?',
      answer: 'Absolutely! Premium members can create custom AI characters with unique personalities, appearances, and backstories.',
    },
    {
      question: 'Is my data private?',
      answer: 'Your privacy is our priority. All conversations are encrypted and stored securely. We never share your personal data with third parties.',
    },
    {
      question: 'What makes lovelybabe.ai different?',
      answer: 'We combine cutting-edge AI technology with beautiful design, offering the most realistic and engaging character interactions available.',
    },
  ];

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError('');
    const { user, error } = await signInWithGoogle();
    if (error) {
      setError(error);
    } else {
      setShowAuthModal(false);
      setEmail('');
      setPassword('');
    }
    setLoading(false);
  };

  const handleTwitterSignIn = async () => {
    setLoading(true);
    setError('');
    const { user, error } = await signInWithTwitter();
    if (error) {
      setError(error);
    } else {
      setShowAuthModal(false);
      setEmail('');
      setPassword('');
    }
    setLoading(false);
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { user, error } = await signInWithEmail(email, password);
    if (error) {
      setError(error);
    } else {
      setShowAuthModal(false);
      setEmail('');
      setPassword('');
    }
    setLoading(false);
  };

  const handleEmailSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    const { user, error } = await signUpWithEmail(email, password);
    if (error) {
      setError(error);
    } else {
      setShowAuthModal(false);
      setEmail('');
      setPassword('');
    }
    setLoading(false);
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }
    setLoading(true);
    setError('');
    setResetPasswordSuccess('');
    const { error } = await resetPassword(email);
    if (error) {
      setError(error);
    } else {
      setResetPasswordSuccess('Password reset email sent! Check your inbox.');
    }
    setLoading(false);
  };

  return (
    <div>
      {showAgeVerification && (
        <div className="age-verification-overlay">
          <div className="age-verification-modal">
            <div className="age-verification-icon">
              <i className="fas fa-shield-alt"></i>
            </div>
            <h2 className="age-verification-title">Confirm your Age</h2>
            <p className="age-verification-text">
              This content may include sensitive or adult material that isn't suitable for everyone. 
              Please confirm your age to continue.
            </p>
            <div className="age-verification-buttons">
              <button 
                className="age-btn age-btn-confirm"
                onClick={handleAgeConfirm}
              >
                I am over 18, Continue
              </button>
              <button 
                className="age-btn age-btn-decline"
                onClick={handleUnderAge}
              >
                I am under 18
              </button>
            </div>
          </div>
        </div>
      )}

      {showCookieConsent && (
        <div className="cookie-consent-banner">
          <div className="cookie-consent-content">
            <h4 className="cookie-consent-title">We use Cookies</h4>
            <p className="cookie-consent-text">
              We use cookies to ensure a seamless browsing experience, tailor content, 
              enhance our website and perform other functions as outlined in our Policy.
            </p>
          </div>
          <div className="cookie-consent-buttons">
            <button 
              className="cookie-btn cookie-btn-all"
              onClick={() => handleCookieConsent('all')}
            >
              Accept All
            </button>
            <button 
              className="cookie-btn cookie-btn-necessary"
              onClick={() => handleCookieConsent('necessary')}
            >
              Accept Only Necessary
            </button>
          </div>
        </div>
      )}

      {showAuthModal && (
        <div 
          className="auth-modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAuthModal(false);
              setError('');
              setResetPasswordSuccess('');
            }
          }}
        >
          <div className="auth-modal">
            <button 
              className="auth-modal-close"
              onClick={() => {
                setShowAuthModal(false);
                setError('');
                setResetPasswordSuccess('');
              }}
            >
              <i className="fas fa-times"></i>
            </button>

            <div className="auth-modal-content">
              <div className="auth-modal-image">
        <Image
                  src={isSignUp ? '/girls/img (7).jpg' : '/girls/img (8).jpg'}
                  alt="Character"
                  fill
                  className="auth-character-img"
                  
                  quality={100}
          priority
        />
                <div className="auth-modal-branding">
                  <div className="auth-modal-logo">
                    <span className="logo-lovley">Lovely</span>
                    <span className="logo-babe">Babe.ai</span>
                  </div>
                </div>
              </div>

              <div className="auth-modal-form">
                <h2 className="auth-modal-title">
                  {isSignUp ? 'Create Account' : 'Sign in'}
                </h2>

                <form onSubmit={isSignUp ? handleEmailSignUp : handleEmailSignIn}>
                  {error && (
                    <div className="auth-error">
                      {error}
                    </div>
                  )}
                  {resetPasswordSuccess && (
                    <div className="auth-success">
                      {resetPasswordSuccess}
                    </div>
                  )}

                  <div className="auth-input-group">
                    <i className="fas fa-envelope auth-input-icon"></i>
                    <input
                      type="email"
                      placeholder="Email"
                      className="auth-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </div>

                  <div className="auth-input-group">
                    <i className="fas fa-lock auth-input-icon"></i>
                    <input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Password"
                      className="auth-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="auth-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                    >
                      <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>

                  {!isSignUp && (
                    <button 
                      type="button" 
                      className="auth-forgot-password"
                      onClick={handleForgotPassword}
                      disabled={loading}
                    >
                      Forget password?
                    </button>
                  )}

                  <button type="submit" className="auth-submit-btn" disabled={loading}>
                    {loading ? 'Please wait...' : (isSignUp ? 'Create Account' : 'Sign in')}
                  </button>
                </form>

                <div className="auth-divider">
                  <span>or sign in with</span>
                </div>

                <div className="auth-social-buttons">
                  <button 
                    className="auth-social-btn google"
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                  >
                    <svg width="18" height="18" viewBox="0 0 18 18">
                      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
                      <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
                      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                    </svg>
                    <span>Google</span>
                  </button>

                  <button 
                    className="auth-social-btn twitter"
                    onClick={handleTwitterSignIn}
                    disabled={loading}
                  >
                    <i className="fab fa-x-twitter"></i>
                  </button>
                </div>

                <p className="auth-terms">
                  By signing up, you agree to Terms of Service
                </p>

                <div className="auth-divider-line"></div>

                <div className="auth-switch">
                  <span className="auth-switch-text">
                    {isSignUp ? 'Already have an account?' : "Don't have an account yet?"}
                  </span>
                  <button
                    className="auth-switch-btn"
                    onClick={() => {
                      setIsSignUp(!isSignUp);
                      setError('');
                      setResetPasswordSuccess('');
                    }}
                  >
                    {isSignUp ? 'Sign in' : 'Sign up'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <header className="top-bar">
        <div className="top-bar-left">
          <button className="hamburger-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <i className="fas fa-bars"></i>
          </button>
          <div className="logo">
            <span className="logo-lovley">Lovely</span>
            <span className="logo-babe">babe.ai</span>
          </div>

          <nav className="top-bar-center">
            <button
              className={`tab-btn ${activeTab === 'girls' ? 'active' : ''}`}
              onClick={() => setActiveTab('girls')}
            >
              <i className="fas fa-venus"></i>
              <span>Girls</span>
            </button>
            <button
              className={`tab-btn ${activeTab === 'boys' ? 'active' : ''}`}
              onClick={() => setActiveTab('boys')}
            >
              <i className="fas fa-mars"></i>
              <span>Boys</span>
            </button>
            <button
              className={`tab-btn ${activeTab === 'anime' ? 'active' : ''}`}
              onClick={() => setActiveTab('anime')}
            >
              <i className="fas fa-star"></i>
              <span>Anime</span>
            </button>
          </nav>
          <div className="top-bar-right-mobile">
          {user ? (
            <>
              <button 
                className="btn-my-account" 
                onClick={() => {
                  if (isNavigating) return;
                  setIsNavigating(true);
                  router.push('/myaccount');
                }}
                disabled={isNavigating}
              >
                My Account
              </button>
              <button 
                className="btn-join-premium" 
                onClick={() => {
                  if (isNavigating) return;
                  setIsNavigating(true);
                  router.push('/vip');
                }}
                disabled={isNavigating}
              >
                Join Premium
              </button>
            </>
          ) : (
            <>
              <button 
                className="btn-create"
                onClick={() => {
                  setIsSignUp(true);
                  setShowAuthModal(true);
                }}
              >
                Create Free Account
              </button>
              <button 
                className="btn-login"
                onClick={() => {
                  setIsSignUp(false);
                  setShowAuthModal(true);
                }}
              >
                Login
              </button>
            </>
          )}
        </div>


        </div>

        <div className="top-bar-right">
          {user ? (
            <>
              <button 
                className="btn-my-account" 
                onClick={() => {
                  if (isNavigating) return;
                  setIsNavigating(true);
                  router.push('/myaccount');
                }}
                disabled={isNavigating}
              >
                My Account
              </button>
              <button 
                className="btn-join-premium" 
                onClick={() => {
                  if (isNavigating) return;
                  setIsNavigating(true);
                  router.push('/vip');
                }}
                disabled={isNavigating}
              >
                Join Premium
              </button>
            </>
          ) : (
            <>
              <button 
                className="btn-create"
                onClick={() => {
                  setIsSignUp(true);
                  setShowAuthModal(true);
                }}
              >
                Create Free Account
              </button>
              <button 
                className="btn-login"
                onClick={() => {
                  setIsSignUp(false);
                  setShowAuthModal(true);
                }}
              >
                Login
              </button>
            </>
          )}
        </div>
      </header>

      <div className="main-layout">
        {/* Sidebar Overlay for Mobile */}
        <div 
          className={`sidebar-overlay ${sidebarOpen ? 'visible' : ''}`}
          onClick={() => setSidebarOpen(false)}
        ></div>

        <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
          <div className="sidebar-content">
            <button className="sidebar-btn active">
              <i className="fas fa-compass"></i>
              <span>Explore</span>
            </button>
            <button 
              className="sidebar-btn"
              onClick={async () => {
                if (!user) {
                  setIsSignUp(false);
                  setShowAuthModal(true);
                } else {
                  // Fetch first chat from user's chat history
                  try {
                    const { collection, query, orderBy, getDocs } = await import('firebase/firestore');
                    const { db } = await import('@/lib/firebase');
                    const chatHistoryRef = collection(db, 'users', user.uid, 'chatHistory');
                    const q = query(chatHistoryRef, orderBy('lastAccessed', 'desc'));
                    const querySnapshot = await getDocs(q);
                    
                    if (!querySnapshot.empty) {
                      const firstChat = querySnapshot.docs[0].data();
                      const isMobile = window.innerWidth <= 968;
                      const gender = firstChat.gender || 'girls';
                      const chatUrl = `/${gender}/${encodeURIComponent(firstChat.modelName)}${isMobile ? '?showChatHistory=true' : ''}`;
                      router.push(chatUrl);
                    } else {
                      alert('No chat history found. Start a conversation first!');
                    }
                  } catch (error) {
                    console.error('Error fetching chat history:', error);
                    alert('Error loading chat history');
                  }
                }
              }}
            >
              <i className="fas fa-comment"></i>
              <span>Chat</span>
            </button>
            <button 
              className="sidebar-btn"
              onClick={() => {
                if (isNavigating) return;
                if (!user) {
                  setIsSignUp(false);
                  setShowAuthModal(true);
                } else {
                  setIsNavigating(true);
                  router.push('/collection');
                }
              }}
              disabled={isNavigating}
            >
              <i className="fas fa-bookmark"></i>
              <span>Collection</span>
            </button>
            <button 
              className="sidebar-btn"
              onClick={() => {
                if (isNavigating) return;
                setIsNavigating(true);
                router.push('/imagegenertion');
              }}
              disabled={isNavigating}
            >
              <i className="fas fa-image"></i>
              <span>Generate Image</span>
            </button>
            <button 
              className="sidebar-btn"
              onClick={() => {
                if (isNavigating) return;
                if (!user) {
                  setIsSignUp(false);
                  setShowAuthModal(true);
                } else {
                  setIsNavigating(true);
                  router.push(`/createcharacter?gender=${activeTab}`);
                }
              }}
              disabled={isNavigating}
            >
              <i className="fas fa-user-plus"></i>
              <span>Create Character</span>
            </button>
            <button 
              className="sidebar-btn"
              onClick={() => {
                if (isNavigating) return;
                if (!user) {
                  setIsSignUp(false);
                  setShowAuthModal(true);
                } else {
                  setIsNavigating(true);
                  router.push('/myai');
                }
              }}
              disabled={isNavigating}
            >
              <i className="fas fa-heart"></i>
              <span>My AI</span>
            </button>


            <button 
              className="sidebar-btn vip-btn"
              onClick={() => {
                if (isNavigating) return;
                if (!user) {
                  setIsSignUp(false);
                  setShowAuthModal(true);
                } else {
                  setIsNavigating(true);
                  router.push('/vip');
                }
              }}
              disabled={isNavigating}
            >
              <i className="fas fa-gem"></i>
              <span>Join VIP</span>
            </button>

            <div className="sidebar-divider"></div>

            <button 
              className="sidebar-btn"
              onClick={() => {
                if (isNavigating) return;
                window.open('https://discord.gg/zQkyVFFa', '_blank');
              }}
            >
              <i className="fab fa-discord"></i>
              <span>Discord</span>
            </button>
            <button 
              className="sidebar-btn" 
              onClick={() => {
                if (isNavigating) return;
                setIsNavigating(true);
                router.push('/helpcenter');
              }}
              disabled={isNavigating}
            >
              <i className="fas fa-question-circle"></i>
              <span>Help Center</span>
            </button>
            <button 
              className="sidebar-btn"
              onClick={() => {
                if (isNavigating) return;
                window.location.href = 'mailto:support@lovelybabe.ai';
              }}
            >
              <i className="fas fa-envelope"></i>
              <span>Contact Us</span>
            </button>
            <button 
              className="sidebar-btn" 
              onClick={() => {
                if (isNavigating) return;
                setIsNavigating(true);
                router.push('/affiliate');
              }}
              disabled={isNavigating}
            >
              <i className="fas fa-handshake"></i>
              <span>Affiliate</span>
            </button>

            <div className="sidebar-divider"></div>

            <div className="sidebar-footer">
              <p>Privacy Notice * Terms and Services</p>
            </div>
          </div>
        </aside>

        <main className="main-content">
          {/* Mobile Tabs Bar - Only visible on mobile */}
          <div className="mobile-tabs-bar">
            <button
              className={`mobile-tab-btn ${activeTab === 'girls' ? 'active' : ''}`}
              onClick={() => setActiveTab('girls')}
            >
              <i className="fas fa-venus"></i>
              <span>Girls</span>
            </button>
            <button
              className={`mobile-tab-btn ${activeTab === 'boys' ? 'active' : ''}`}
              onClick={() => setActiveTab('boys')}
            >
              <i className="fas fa-mars"></i>
              <span>Boys</span>
            </button>
            <button
              className={`mobile-tab-btn ${activeTab === 'anime' ? 'active' : ''}`}
              onClick={() => setActiveTab('anime')}
            >
              <i className="fas fa-star"></i>
              <span>Anime</span>
            </button>
          </div>

          <div className="banner-top">
            <Image
              src={isMobile ? "/girls/mobiletopbanner.png" : "/girls/bannertopgirl.png"}
              alt="Featured Banner"
              fill
              className="banner-img"
              priority
              quality={100}
              sizes="(max-width: 968px) 100vw, 1000px"
            />
          </div>

          <div className="section-header">
            <h2>
              <span className="brand-white">Lovelybabe Ai</span>
              <span className="brand-pink"> Characters</span>
            </h2>
          </div>

          <div className="character-grid">
            {modelsLoading ? (
              // Shimmer loading effect
              <>
                {[...Array(isMobile ? 4 : 8)].map((_, index) => (
                  <div key={index} className="character-card shimmer-card">
                    <div className="character-image shimmer-wrapper">
                      <div className="shimmer-placeholder"></div>
                      <div className="character-overlay shimmer-overlay">
                        <div className="shimmer-text shimmer-title"></div>
                        <div className="shimmer-text shimmer-description"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            ) : models.length === 0 ? (
              <div className="models-empty">
                <i className="fas fa-inbox"></i>
                <p>No models available for {activeTab} yet.</p>
                <p className="empty-subtext">Check back soon or try another category!</p>
              </div>
            ) : (
              <>
              {displayedModels.map((model, index) => (
                <div 
                  key={model.id} 
                  className="character-card"
                  data-model-id={model.id}
                  onClick={() => {
                    if (isNavigating) return;
                    setIsNavigating(true);
                    router.push(`/${model.gender}/${encodeURIComponent(model.name)}`);
                  }}
                  onMouseEnter={(e) => {
                    if (window.innerWidth > 968) {
                      const video = e.currentTarget.querySelector('video') as HTMLVideoElement;
                      if (video && videoReadyStates[model.id]) {
                        // Pause all other videos
                        document.querySelectorAll('.character-video').forEach((v) => {
                          if (v !== video) (v as HTMLVideoElement).pause();
                        });
                        video.play().catch(() => {});
                        setActiveVideoId(model.id);
                      }
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (window.innerWidth > 968) {
                      const video = e.currentTarget.querySelector('video') as HTMLVideoElement;
                      if (video) {
                        video.pause();
                        video.currentTime = 0;
                        if (activeVideoId === model.id) {
                          setActiveVideoId(null);
                        }
                      }
                    }
                  }}
                  onTouchStart={(e) => {
                    if (window.innerWidth <= 968) {
                      const video = e.currentTarget.querySelector('video') as HTMLVideoElement;
                      if (video && videoReadyStates[model.id]) {
                        // Pause all other videos first
                        document.querySelectorAll('.character-video').forEach((v) => {
                          if (v !== video) {
                            (v as HTMLVideoElement).pause();
                            (v as HTMLVideoElement).currentTime = 0;
                          }
                        });
                        
                        // Play this video
                        const playPromise = video.play();
                        if (playPromise !== undefined) {
                          playPromise.catch((error) => {
                            console.log('Video play failed:', error);
                          });
                        }
                        setActiveVideoId(model.id);
                      }
                    }
                  }}
                >
                  <div className="character-image">
                    {model.isNew && (
                      <span className="badge-new">
                        <i className="fas fa-bolt"></i>
                        New
                      </span>
                    )}
                    {model.storyTag && (
                      <span className="badge-story">
                        <i className="fas fa-book"></i>
                        {model.storyTag}
                      </span>
                    )}
                    <Image
                      src={getModelImageUrl(model.mainImagePath)}
                      alt={model.name}
                      fill
                      className={`character-img ${videoReadyStates[model.id] ? 'has-video-ready' : ''} ${activeVideoId === model.id ? 'video-playing' : ''}`}
                      sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 25vw"
                      quality={100}
                      unoptimized={model.mainImagePath.startsWith('/models/')}
                    />
                    {model.idleVideoPath && (
                      <video
                        className={`character-video ${videoReadyStates[model.id] ? 'video-ready' : ''} ${activeVideoId === model.id ? 'is-playing' : ''}`}
                        src={getModelVideoUrl(model.idleVideoPath)}
                        loop
                        muted
                        playsInline
                        webkit-playsinline="true"
                        x5-playsinline="true"
                        preload={index < 4 ? 'auto' : 'metadata'}
                        onLoadedData={(e) => {
                          setVideoReadyStates(prev => ({
                            ...prev,
                            [model.id]: true
                          }));
                        }}
                      />
                    )}
                    <div className="character-overlay">
                      <h3>
                        {model.name} {model.age && <span className="character-age">{model.age}</span>}
                      </h3>
                      <p>{model.shortDescription}</p>
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Load More Trigger */}
              {hasMoreToLoad && (
                <div className="load-more-trigger" style={{ gridColumn: '1 / -1', height: '1px' }}></div>
              )}
              
              {/* Loading More Indicator */}
              {loadingMore && (
                <>
                  {[...Array(isMobile ? 4 : 8)].map((_, index) => (
                    <div key={`loading-${index}`} className="character-card shimmer-card">
                      <div className="character-image shimmer-wrapper">
                        <div className="shimmer-placeholder"></div>
                        <div className="character-overlay shimmer-overlay">
                          <div className="shimmer-text shimmer-title"></div>
                          <div className="shimmer-text shimmer-description"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
              </>
            )}
          </div>

          <div className="banner-bottom">
            <div className="banner-placeholder">Promotional Banner</div>
          </div>

          <div className="faq-section">
            <h2 className="faq-title">Loveleybabe - FAQ</h2>
            <div className="faq-container">
              {faqData.map((faq, index) => (
                <div key={index} className="faq-item">
                  <h3 className="faq-question">{faq.question}</h3>
                  <p className="faq-answer">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="description-section">
            <h2>About Lovelybabe.ai</h2>
            <p>
              Welcome to Lovelybabe.ai, the premier destination for AI character interactions. 
              Our platform combines advanced artificial intelligence with stunning visual design 
              to create the most immersive and engaging character experience available today.
            </p>
            <p>
              Whether you are looking for meaningful conversations, creative roleplay, or simply 
              want to explore the possibilities of AI companionship, Lovelybabe.ai offers a safe, 
              private, and innovative environment. Our diverse roster of characters spans multiple 
              categories, each with unique personalities and traits designed to provide authentic 
              and memorable interactions.
            </p>
            <p>
              Join thousands of users who have already discovered the magic of AI character 
              interactions. Create your free account today and start your journey with 
              Lovelybabe.ai. Premium features unlock even more possibilities, including custom 
              character creation, enhanced chat capabilities, and exclusive content.
            </p>
          </div>

          <footer className="footer">
            <div className="footer-content">
            
              <div className="footer-section">
                <h4>Support</h4>
                <p onClick={() => router.push('/helpcenter')} style={{cursor: 'pointer'}}>Help Center</p>
                <p onClick={() => router.push('mailto:support@lovelybabe.ai')} style={{cursor: 'pointer'}}>Contact Us</p>
                
              </div>
              <div className="footer-section">
                <h4>Legal</h4>
                <p onClick={() => router.push('/terms')} style={{cursor: 'pointer'}}>Terms of Service</p>
                <p onClick={() => router.push('/privacy')} style={{cursor: 'pointer'}}>Privacy Policy</p>
                <p onClick={() => router.push('/cookies')} style={{cursor: 'pointer'}}>Cookie Policy</p>
              </div>
              <div className="footer-section">
                <h4>Community</h4>
                <p onClick={() => router.push('https://discord.gg/zQkyVFFa')} style={{cursor: 'pointer'}}>Discord</p>
              
                <p onClick={() => router.push('https://www.instagram.com/lovelybabe.ai/reels/?hl=en')} style={{cursor: 'pointer'}}>Instagram</p>
              </div>
            </div>
            <div className="footer-bottom">
              <p>&copy; 2025 Lovelybabe.ai. All rights reserved.</p>
        </div>
          </footer>
      </main>
      </div>
    </div>
  );
}
